import { ChevronRightIcon } from '@heroicons/react/20/solid';
import { PhoneIcon } from '@heroicons/react/24/outline';

export interface IProps {
    /** The line above the number, e.g. "Speak to our finance team". */
    label: string;
    /**
     * The partner's number, from config. Never written into this component or
     * the copy layer — a number in code goes stale the day the partner
     * changes theirs.
     */
    phone: string;
}

export const testId = 'PhoneCallBar';

/**
 * A wide call-to-phone bar: icon, the reason to ring, the number set large,
 * and a chevron.
 *
 * Not a `Button`. That component is a single-line `<button>` fixed at `h-8`
 * with `rounded-full` and a `<p>` around its text, and every one of those
 * fights this: two stacked lines, a taller bar, a square-ish radius, and an
 * `<a href="tel:">` rather than a click handler. Adding it to `EButtonType`
 * would mean a variant that has to unset most of the base class, which is
 * how a button component stops being one.
 *
 * `<a>` rather than `<button>` is the point of it: a real link long-presses,
 * right-clicks and copies like a phone number, on every platform, for free.
 *
 * It reports nothing. The one screen using it is a holding screen nobody
 * reaching it has entered a journey from, and a new click event would be a
 * tracking change. Give it tracking when a screen needs it, not before.
 *
 * Takes its ground from the partner's own colour, so it themes itself for
 * any brand. It assumes that colour is dark enough for white text, the same
 * assumption `ContactPanel` makes of the secondary colour.
 */
export function PhoneCallBar({ label, phone }: IProps): JSX.Element {
    return (
        <a
            data-testid={testId}
            // Spaces are legal in a tel: href but not every dialler is
            // forgiving, so the digits go through clean.
            href={`tel:${phone.replace(/\s/g, '')}`}
            // shadow-callBar is mixed from the partner's colour, so every
            // brand lifts off the page by the same amount.
            className='relative _flex w-full items-center gap-3 overflow-hidden rounded-2xl bg-primary px-4 py-4 text-white shadow-callBar hover:bg-primaryHover'
        >
            {/* A light sweeping across the bar every few seconds. Purely
                decorative, so it is hidden from assistive tech and cannot
                take the pointer away from the link underneath it. */}
            <span
                aria-hidden='true'
                className='pointer-events-none absolute inset-y-0 left-0 w-[30%] animate-sheen bg-gradient-to-r from-transparent via-white/20 to-transparent motion-reduce:animate-none'
            />

            {/* The handset shakes briefly on a long cycle — a prompt rather
                than a permanent wobble. */}
            <span className='flex-center h-11 w-11 shrink-0 animate-handset rounded-xl bg-white/20 motion-reduce:animate-none'>
                <PhoneIcon className='h-5 w-5' />
            </span>
            <span className='_flex min-w-0 flex-col text-left'>
                <span className='text-[13px] leading-snug text-white/80'>
                    {label}
                </span>
                <span className='text-[22px] font-bold leading-tight'>
                    {phone}
                </span>
            </span>
            <ChevronRightIcon className='ml-auto h-5 w-5 shrink-0' />
        </a>
    );
}
